import type { StrainSummary } from '@shared';

export interface DoseSelection {
  product: StrainSummary;
  weightInput: string;
}

export interface DoseContribution {
  productId: string;
  weightGrams: number;
  thcMg: number;
  cbdMg: number | null;
}

export interface DoseSummary {
  contributions: DoseContribution[];
  totalWeightGrams: number;
  totalThcMg: number;
  totalCbdMg: number | null;
  effectiveThcPercent: number | null;
  selectedCount: number;
}

export type DoseCalculatorAction =
  | { type: 'add'; product: StrainSummary }
  | { type: 'remove'; productId: string }
  | { type: 'set_weight'; productId: string; value: string }
  | { type: 'clear' };

export function isUsableThcPercent(value: number): boolean {
  return Number.isFinite(value) && value > 0 && value <= 100;
}

export function isAvailableCbdPercent(
  value: number | null | undefined,
): value is number {
  return value != null && Number.isFinite(value) && value >= 0 && value <= 100;
}

export function parseDoseWeight(value: string | number): number {
  if (typeof value === 'string' && value.trim() === '') return 0;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

export function cannabinoidMg(
  weightGrams: string | number,
  percent: number | null | undefined,
): number | null {
  if (percent == null || !Number.isFinite(percent) || percent < 0 || percent > 100) {
    return null;
  }
  return parseDoseWeight(weightGrams) * percent * 10;
}

export function thcMgContribution(
  weightGrams: string | number,
  thcPercent: number,
): number {
  if (!isUsableThcPercent(thcPercent)) return 0;
  return cannabinoidMg(weightGrams, thcPercent) ?? 0;
}

export function cbdMgContribution(
  weightGrams: string | number,
  cbdPercent: number | null | undefined,
): number | null {
  if (!isAvailableCbdPercent(cbdPercent)) return null;
  return cannabinoidMg(weightGrams, cbdPercent);
}

export function summarizeDose(selections: DoseSelection[]): DoseSummary {
  const contributions = selections.map(({ product, weightInput }) => ({
    productId: product.id,
    weightGrams: parseDoseWeight(weightInput),
    thcMg: thcMgContribution(weightInput, product.thcPercent),
    cbdMg: cbdMgContribution(weightInput, product.cbdPercent),
  }));
  const totalWeightGrams = contributions.reduce(
    (sum, contribution) => sum + contribution.weightGrams,
    0,
  );
  const totalThcMg = contributions.reduce(
    (sum, contribution) => sum + contribution.thcMg,
    0,
  );
  const cbdContributions = contributions
    .map(({ cbdMg }) => cbdMg)
    .filter((value): value is number => value !== null);
  const totalCbdMg =
    cbdContributions.length > 0
      ? cbdContributions.reduce((sum, value) => sum + value, 0)
      : null;
  const effectiveThcPercent =
    totalWeightGrams > 0 && Number.isFinite(totalThcMg)
      ? totalThcMg / (totalWeightGrams * 10)
      : null;

  return {
    contributions,
    totalWeightGrams,
    totalThcMg,
    totalCbdMg,
    effectiveThcPercent,
    selectedCount: selections.length,
  };
}

export function doseCalculatorReducer(
  state: DoseSelection[],
  action: DoseCalculatorAction,
): DoseSelection[] {
  switch (action.type) {
    case 'add':
      if (
        !isUsableThcPercent(action.product.thcPercent) ||
        state.some(({ product }) => product.id === action.product.id)
      ) {
        return state;
      }
      return [...state, { product: action.product, weightInput: '' }];
    case 'remove':
      return state.filter(({ product }) => product.id !== action.productId);
    case 'set_weight':
      return state.map((selection) =>
        selection.product.id === action.productId
          ? { ...selection, weightInput: action.value }
          : selection,
      );
    case 'clear':
      return [];
  }
}

export function doseProductDisplayName(product: StrainSummary): string {
  const brand = product.brand?.trim();
  const productName = product.productName?.trim();
  if (brand && productName) return `${brand} - ${productName}`;
  return product.name?.trim() || productName || brand || 'Unnamed product';
}

export function doseProductMatchesSearch(
  product: StrainSummary,
  search: string,
): boolean {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return [
    product.name,
    product.brand,
    product.productName,
    product.productId,
    product.strainClassSourceText,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(query);
}
